import dayjs from "dayjs";
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography, CircularProgress, Chip } from "@material-ui/core";
import { Assignment, Equalizer, TurnedIn, EmojiEvents, DateRange } from "@material-ui/icons";

import { apiCall } from "../../../../utils/api";
import { API_URLS } from "../../../../constants/api";
import { KPTextfield } from "../../../../components/KPTComponents";
import { notification } from "reducer/home/action";
import select from "../../../../utils/select";
import SectionCard from "../../utils/SectionCard";

// SettingForm — cấu hình 4 mốc thời gian (Đăng ký / Báo cáo tiến độ / Nghiệm
// thu cấp khoa / Hội nghị cấp trường) cho 1 năm học.
//
// BE: model `YearlyDueDate` lưu 4 cặp regStartTime/regEndTime,
// reportStartTime/reportEndTime, acceptStartTime/acceptEndTime,
// conferenceStartTime/conferenceEndTime. Endpoint PUT /due/upsert
// (xem yearly_due_date_controller.go) — upsert theo (schoolYear, type="student").
//
// CHUẨN MÚI GIỜ:
//   - FE gửi datetime kèm offset +07:00:
//       start = "YYYY-MM-DDT00:00:00+07:00"
//       end   = "YYYY-MM-DDT23:59:59+07:00"
//   - BE lưu UTC, so sánh trực tiếp với time.Now().
//   - KHÔNG dùng valueFormat.stringToDate vì hàm đó ghép sai (UTC midnight
//     thay vì VN midnight) → lệch 7 giờ, không phù hợp khi BE đã bỏ -7h hack.
//
// ENFORCEMENT:
//   - BE: SubmitRegistration / SubmitProgressReport / SubmitAcceptance /
//     AssignCouncil(final) gọi YearlyDueDateService.CheckWindow → trả 400
//     nếu ngoài hạn (xem service/yearly_due_date_service.go).
//   - conferenceStart/End được dùng cho "Gán hội đồng nghiệm thu".
function SettingForm(props) {
  const dispatch = useDispatch();
  const profileDetail = useSelector((s) => select(s, "profileReducer", "detail"));
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);

  // 4 nhóm cấu hình. Refactor 2026-08-26: bỏ BCTĐ đợt 2 → chỉ còn 1 cặp
  // deadline báo cáo tiến độ (report1*). order index dùng cho ordering warning.
  const sections = [
    { icon: <Assignment />,  title: "Đăng ký hồ sơ",            startKey: "regStart",        endKey: "regEnd",        beStart: "regStartTime",        beEnd: "regEndTime" },
    { icon: <Equalizer />,   title: "Báo cáo tiến độ",          startKey: "report1Start",    endKey: "report1End",    beStart: "report1StartTime",    beEnd: "report1EndTime" },
    { icon: <TurnedIn />,    title: "Nghiệm thu cấp khoa",      startKey: "acceptStart",     endKey: "acceptEnd",     beStart: "acceptStartTime",     beEnd: "acceptEndTime" },
    { icon: <EmojiEvents />, title: "Hội nghị cấp trường",      startKey: "conferenceStart", endKey: "conferenceEnd", beStart: "conferenceStartTime", beEnd: "conferenceEndTime" },
  ];

  // Refactor 2026-05-15: BE cột YearlyDueDate giờ là DATE (không có time +
  // timezone). Đọc từ BE: ISO timestamp "2026-05-15T00:00:00Z" — lấy substring
  // first 10 ký tự (YYYY-MM-DD) để tránh shift ngày khi browser khác TZ.
  const formatBEDate = (iso) => {
    if (!iso) return "";
    const datePart = String(iso).substring(0, 10); // "YYYY-MM-DD"
    const [y, m, d] = datePart.split("-");
    if (!y || !m || !d) return "";
    return `${d}/${m}/${y}`;
  };

  // Parse "DD/MM/YYYY" → dayjs để so sánh / status chip.
  const parseDDMMYYYY = (s) => {
    if (!s || typeof s !== "string") return null;
    const [d, m, y] = s.split("/");
    if (!d || !m || !y) return null;
    const dj = dayjs(`${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`);
    return dj.isValid() ? dj : null;
  };

  // "DD/MM/YYYY" → ISO date UTC midnight cho BE.
  // Schema mới: BE cột DATE — chỉ lấy date portion khi Postgres convert.
  // Gửi "YYYY-MM-DDT00:00:00Z" để Go json.Unmarshal RFC3339 thành công.
  // Cùng helper dùng cho cả Start lẫn End (không cần phân biệt giờ vì DATE
  // không lưu time).
  const dateOnlyISO = (s) => {
    if (!s) return null;
    const [d, m, y] = s.split("/");
    if (!d || !m || !y) return null;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}T00:00:00Z`;
  };

  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const api = API_URLS.YEARLY_DUE_DATE.filterYearlyDueDate();
      const { response, error } = await apiCall({
        ...api,
        payload: { type: "student", schoolYear: props.year },
      });
      if (!error && response.status === 200 && response.data.success) {
        const res = response.data.data[0] || {};
        const next = { ...res };
        sections.forEach((s) => {
          next[s.startKey] = formatBEDate(res?.[s.beStart]);
          next[s.endKey] = formatBEDate(res?.[s.beEnd]);
        });
        setValues(next);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.year]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleChange = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  // ---- Status chip: tính trạng thái 1 section dựa trên start/end + now ----
  const sectionStatus = (s) => {
    const start = parseDDMMYYYY(values?.[s.startKey]);
    const end = parseDDMMYYYY(values?.[s.endKey]);
    if (!start || !end) return { label: "Chưa cấu hình", color: "default", bg: "#bdbdbd" };
    const now = dayjs();
    if (now.isBefore(start, "day")) {
      const days = start.diff(now, "day");
      return { label: `Sắp mở (còn ${days} ngày)`, color: "default", bg: "#ff9800" };
    }
    if (now.isAfter(end, "day")) return { label: "Đã đóng", color: "default", bg: "#9e9e9e" };
    return { label: "Đang mở", color: "default", bg: "#4caf50" };
  };

  // 2026-08-11: bỏ ràng buộc thứ tự GIỮA các mốc (vd "báo cáo tiến độ phải
  // sau ngày đăng ký") — vẫn giữ check end >= start TRONG cùng 1 mốc theo
  // yêu cầu.
  const validate = () => {
    for (const s of sections) {
      const sv = values?.[s.startKey];
      const ev = values?.[s.endKey];
      // Cho phép để trống cả 2 (mốc chưa cấu hình). Nếu chỉ 1 đầu → lỗi.
      if (!sv && !ev) continue;
      if (!sv || !ev) return `Mốc "${s.title}" thiếu ngày bắt đầu hoặc kết thúc.`;
      const sd = parseDDMMYYYY(sv);
      const ed = parseDDMMYYYY(ev);
      if (!sd || !ed) return `Ngày không hợp lệ ở mốc "${s.title}".`;
      if (ed.isBefore(sd, "day")) return `Ngày kết thúc phải ≥ ngày bắt đầu ở mốc "${s.title}".`;
    }
    return null;
  };

  const updateData = useCallback(
    async (payload) => {
      setLoading(true);
      const api = API_URLS.YEARLY_DUE_DATE.updateYearlyDueDate();
      try {
        const { response, error } = await apiCall({ ...api, payload });
        if (!error && response.status === 200 && response.data.success) {
          dispatch(notification({ type: "success", message: "Cập nhật thời gian thành công!" }));
          props.handleClose();
        } else {
          // BE trả 400 (axios throw) → error.response.data.message. BE trả
          // 200 success=false → response.data.message. Fallback message
          // generic nếu cả hai cùng vắng.
          const msg =
            error?.response?.data?.message ||
            response?.data?.message ||
            "Cập nhật thời gian thất bại!";
          dispatch(notification({ type: "error", message: msg }));
        }
      } finally {
        setLoading(false);
      }
    },
    [dispatch, props]
  );

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    const reason = validate();
    if (reason) {
      dispatch(notification({ type: "warning", message: reason }));
      return;
    }
    const payload = {
      ...values,
      year: props.year,
      schoolYear: props.year,
      type: "student",
      // Audit log (xem yearly_due_date_history). BE gắn vào snapshot khi insert.
      updatedById: profileDetail?.id || 0,
      updatedByName: profileDetail?.name || "",
    };
    sections.forEach((s) => {
      payload[s.beStart] = dateOnlyISO(values?.[s.startKey]);
      payload[s.beEnd] = dateOnlyISO(values?.[s.endKey]);
    });
    updateData(payload);
  };

  if (loading && !values) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" id={props.id} onSubmit={handleSubmit} padding="16px 24px">
      {/* Header — năm học áp dụng */}
      <Box
        display="flex"
        alignItems="center"
        padding="8px 16px"
        bgcolor="#e3f2fd"
        border="solid 1px #90caf9"
        borderRadius={4}
      >
        <DateRange style={{ marginRight: 12, color: "#1976d2" }} />
        <Typography variant="body2">
          Đang cấu hình cho năm học{" "}
          <b>
            {props.year} – {props.year + 1}
          </b>
          . Đổi năm ở thanh phía trên trước khi mở Cấu hình.
        </Typography>
      </Box>

      {/* 4 mốc thời gian */}
      {sections.map((s) => {
        const status = sectionStatus(s);
        return (
          <SectionCard
            key={s.startKey}
            icon={s.icon}
            title={
              <Box display="flex" alignItems="center" gridGap={12} width="100%">
                <span>{s.title}</span>
                <Chip
                  label={status.label}
                  size="small"
                  style={{
                    backgroundColor: status.bg,
                    color: "#fff",
                    fontWeight: 500,
                    height: 22,
                    fontSize: 12,
                  }}
                />
              </Box>
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <KPTextfield
                  id={s.startKey}
                  type="date"
                  label="Ngày bắt đầu"
                  value={values?.[s.startKey] || ""}
                  handleChangeForm={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <KPTextfield
                  id={s.endKey}
                  type="date"
                  label="Ngày kết thúc"
                  value={values?.[s.endKey] || ""}
                  handleChangeForm={handleChange}
                />
              </Grid>
            </Grid>
          </SectionCard>
        );
      })}
    </Box>
  );
}

export default SettingForm;
